import { createReducer } from '@reduxjs/toolkit'
import { ROOT_NODE_ID } from '../store/migrations/data-structure-conversion'
import { tutorial } from '../utils/tutorial'
import { isNodeTypeOpen, isNodeTypeClosed } from '../utils/nodeTypeGroups'
import { addUnsyncedNodeId } from '../controllers/treeEngineSync'
import { NODE } from './actionTypes'

// Actions

export function postNode(body) {
    return {
        type: NODE.POST.REQUEST,
        payload: {
            body
        }
    }
}

export function patchNode(id, body) {
    return {
        type: NODE.PATCH_CONTENT.REQUEST,
        payload: {
            id,
            body
        }
    }
}

export function deleteNode(id) {
    return {
        type: NODE.DELETE.REQUEST,
        payload: {
            id
        }
    }
}

export function toggleNode(id) {
    return {
        type: NODE.TOGGLE,
        payload: {
            id
        }
    }
}

export function setLocation(id, previousSiblingId, nextSiblingId, parentId) {
    return {
        type: NODE.PATCH_LOCATION.REQUEST,
        payload: {
            id,
            previousSiblingId,
            nextSiblingId,
            parentId
        }
    }
}

export function setParent(id, parentId) {
    return setLocation(id, undefined, undefined, parentId)
}

export function setPreviousSibling(id, previousSiblingId) {
    return setLocation(id, previousSiblingId, undefined, undefined)
}

export function setNextSibling(id, nextSiblingId) {
    return setLocation(id, undefined, nextSiblingId, undefined)
}

export function setNodes(nodes) {
    return {
        type: NODE.BATCH_UPDATE,
        payload: { nodes }
    }
}

// Reducer

// Example of state
// const state = {
//     [node.id]: {
//         id,
//         ...
//     }
// }

const initialState = tutorial

export const nodeReducer = createReducer(initialState, {
    [NODE.POST.REQUEST]: (state, action) => {
        const { id, type, text, previousSiblingId, nextSiblingId } = action.payload.body
        let { parentId } = action.payload.body
        const node = {}

        node.id = id
        node.type = type || 'checkbox'
        node.text = text || ''
        node.childrenIds = []

        if (!parentId) {
            if (previousSiblingId) {
                parentId = state[previousSiblingId].parentId
            } else if (nextSiblingId) {
                parentId = state[nextSiblingId].parentId
            } else {
                parentId = ROOT_NODE_ID
            }
        }

        node.parentId = parentId
        const siblingIds = state[parentId].childrenIds

        if (previousSiblingId) {
            const index = siblingIds.indexOf(previousSiblingId)
            siblingIds.splice(index + 1, 0, id) // add id to siblings after previousSiblingId
        } else if (nextSiblingId) {
            const index = siblingIds.indexOf(nextSiblingId)
            siblingIds.splice(index, 0, id) // add id to siblings before nextSiblingId
        } else {
            siblingIds.push(id)
        }

        state[node.id] = node
        addUnsyncedNodeId(node.id)
        state[parentId].childrenIds = siblingIds
        addUnsyncedNodeId(parentId)
    },

    [NODE.PATCH_CONTENT.REQUEST]: (state, action) => {
        const { id } = action.payload
        state[id] = { ...state[id], ...action.payload.body }
        addUnsyncedNodeId(id)
    },

    [NODE.DELETE.REQUEST]: (state, action) => {
        const { id } = action.payload
        if (state[id].childrenIds.length === 0) {
            const { parentId } = state[id]
            const siblingsIds = state[parentId].childrenIds
            state[parentId].childrenIds = siblingsIds.filter((siblingId) => siblingId !== id)
            addUnsyncedNodeId(parentId)

            delete state[id]
            addUnsyncedNodeId(id)
        } else {
            console.error('Cannot remove node if it has children')
        }
    },

    [NODE.TOGGLE]: (state, action) => {
        const { type } = state[action.payload.id]
        if (isNodeTypeOpen(type)) {
            state[action.payload.id].type = 'done'
            addUnsyncedNodeId(action.payload.id)
        } else if (isNodeTypeClosed(type)) {
            state[action.payload.id].type = 'checkbox'
            addUnsyncedNodeId(action.payload.id)
        }
    },

    [NODE.PATCH_LOCATION.REQUEST]: (state, action) => {
        const { id, previousSiblingId, nextSiblingId } = action.payload
        let { parentId } = action.payload

        // do nothing if no location specified
        if (!parentId && !previousSiblingId && !nextSiblingId) return

        const siblingId = previousSiblingId || nextSiblingId
        if (!parentId) parentId = state[siblingId].parentId

        // change parent if needed
        const oldParentId = state[id].parentId

        if (oldParentId !== parentId) {
            // remove self from old parent's children
            let oldSiblingIds = state[oldParentId].childrenIds
            oldSiblingIds = oldSiblingIds.filter((sibling) => sibling !== id)
            state[oldParentId].childrenIds = oldSiblingIds
            addUnsyncedNodeId(oldParentId)

            // change own parent
            state[id].parentId = parentId
            addUnsyncedNodeId(id)
        }

        // add self to parent's children
        if (!siblingId) {
            // if no sibling specified put to last
            const newSiblingIds = state[parentId].childrenIds
            newSiblingIds.push(id)
            state[parentId].childrenIds = newSiblingIds
            addUnsyncedNodeId(parentId)
        } else {
            const after = !!previousSiblingId
            let siblingids = state[parentId].childrenIds

            // Mutate state only if new sibling is a sibling already
            if (siblingids.includes(siblingId)) {
                siblingids = siblingids.filter((sibling) => sibling !== id)
                const index = siblingids.indexOf(siblingId) + Number(after)
                siblingids.splice(index, 0, id) // add id to siblings at index
                state[parentId].childrenIds = siblingids
                addUnsyncedNodeId(parentId)
            }
        }
    },

    [NODE.BATCH_UPDATE]: (state, action) => {
        const { nodes } = action.payload
        Object.keys(state).forEach((key) => {
            delete state[key]
        })

        Object.keys(nodes).forEach((key) => {
            state[key] = nodes[key]
        })
    }
})

// Saga
export function* nodeSaga() {}

// Selectors
export const selectNodeById = (id) => {
    return (state) => {
        // duct tape
        if (!id || !state.nodes[id]) return null

        return Object.create(state.nodes[id], {
            previousSibling: {
                get: () => selectNodeById(selectPreviousSiblingId(id)(state))(state)
            },
            previousSiblingId: {
                get: () => selectPreviousSiblingId(id)(state)
            },
            nextSibling: {
                get: () => selectNodeById(selectNextSiblingId(id)(state))(state)
            },
            nextSiblingId: {
                get: () => selectNextSiblingId(id)(state)
            },
            parent: {
                get: () => selectNodeById(selectParentId(id)(state))(state)
            },
            children: {
                get: () => state.nodes[id].childrenIds.map((id) => selectNodeById(id)(state))
            },
            siblings: {
                get: () => selectNodeById(selectParentId(id)(state))(state).children
            },
            siblingIds: {
                get: () => selectNodeById(selectParentId(id)(state))(state).childrenIds
            }
        })
    }
}

export const selectParentId = (id) => {
    return (state) => {
        return selectNodeById(id)(state).parentId
    }
}

export const selectPreviousSiblingId = (id) => {
    return (state) => {
        const parentId = selectParentId(id)(state)
        const siblingIds = selectNodeById(parentId)(state).childrenIds
        const siblingIndex = siblingIds.findIndex((siblingId) => siblingId === id)
        if (siblingIndex === 0) return undefined
        return siblingIds[siblingIndex - 1]
    }
}

export const selectNextSiblingId = (id) => {
    return (state) => {
        const parentId = selectParentId(id)(state)
        const siblingIds = selectNodeById(parentId)(state).childrenIds
        const siblingIndex = siblingIds.findIndex((siblingId) => siblingId === id)
        if (siblingIndex === siblingIds.length - 1) return undefined
        return siblingIds[siblingIndex + 1]
    }
}

export const selectSiblings = (id) => {
    return (state) => {
        const parentId = selectParentId(id)(state)
        const siblingIds = selectNodeById(parentId)(state).childrenIds
        return siblingIds.map((id) => selectNodeById(id)(state))
    }
}

export const selectRootNodeChildren = (state) => {
    return state.nodes[ROOT_NODE_ID].childrenIds.map((id) => selectNodeById(id)(state))
}

export const selectBestChildIdFor = (id) => {
    return (state) => {
        if (!id) return

        const node = selectNodeById(id)(state)
        if (!node) return
        let candidates = node.children

        const typePriorities = {
            checkbox: 0,
            idea: 1,
            waiting: 2,
            failed: 3,
            text: 4,
            cancelled: 5,
            done: 6
        }

        candidates = candidates
            .map((item, index) => {
                item.index = index
                return item
            })
            .sort((a, b) => {
                const typeDifference = typePriorities[a.type] - typePriorities[b.type]

                if (typeDifference !== 0) {
                    return typeDifference
                } else {
                    // if done or cancelled return last
                    if (a.type === 'done' || a.type === 'cancelled') {
                        return b.index - a.index
                    }
                    return a.index - b.index
                }
            })
            .map((item) => {
                delete item.index
                return item
            })

        return candidates[0]?.id
    }
}
